import { memo } from "react";
import { Link } from "react-router-dom";
import Users from "lucide-react/dist/esm/icons/users";
import Maximize from "lucide-react/dist/esm/icons/maximize";
import ArrowRight from "lucide-react/dist/esm/icons/arrow-right";
import { ApartmentWithTranslation } from "@/hooks/useApartments";
import apartmentImg from "@/assets/apartment-interior.jpg";
import { getOptimizedImageUrl, getImageSrcSet } from "@/lib/imageUrl";

interface ApartmentCardProps {
  apartment: ApartmentWithTranslation;
  index: number;
  basePath?: string;
}

/**
 * Karten liegen unterhalb des Folds. Einblendung erfolgt per CSS
 * (keine Framer-Motion-Wrapper mehr) — spart Motion-Bundle-Nutzung im
 * initialen Render und vermeidet zusätzliche Composite-Layer.
 * Reale sizes-Angaben orientieren sich am tatsächlichen Grid (5-spaltig auf xl).
 */
const ApartmentCard = memo(({ apartment, basePath }: ApartmentCardProps) => {
  const path = basePath
    ? `${basePath}/${apartment.id}`
    : apartment.house === "strandnixe"
      ? `/ferienwohnungen/haus-strandnixe-doese/${apartment.id}`
      : `/ferienwohnungen/haus-panorama-duhnen/${apartment.id}`;

  return (
    <Link
      to={path}
      className="group block bg-card rounded-2xl overflow-hidden border border-border/50 hover:border-accent/30 shadow-md hover:shadow-xl transition-shadow duration-300 hover:-translate-y-1 will-change-transform"
    >
      <div className="relative h-52 overflow-hidden">
        <img
          src={getOptimizedImageUrl(apartment.image, { width: 640, height: 416 }) || apartmentImg}
          srcSet={getImageSrcSet(apartment.image, [320, 480, 640, 800], { height: undefined }) || undefined}
          sizes="(max-width: 640px) 92vw, (max-width: 768px) 46vw, (max-width: 1024px) 30vw, (max-width: 1280px) 23vw, 18vw"
          alt={apartment.name}
          width={640}
          height={416}
          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute top-3 left-3">
          <span className="bg-accent text-accent-foreground px-3 py-1 rounded-lg text-xs font-body font-bold tracking-wide shadow-sm">
            Nr. {apartment.number}
          </span>
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-2">
          {apartment.maxGuests > 0 && (
            <span className="flex items-center gap-1 bg-background/90 backdrop-blur-sm text-foreground px-2.5 py-1 rounded-lg text-xs font-body shadow-sm">
              <Users className="w-3 h-3" /> {apartment.maxGuests}
            </span>
          )}
          {apartment.size && (
            <span className="flex items-center gap-1 bg-background/90 backdrop-blur-sm text-foreground px-2.5 py-1 rounded-lg text-xs font-body shadow-sm">
              <Maximize className="w-3 h-3" /> {apartment.size}
            </span>
          )}
        </div>
      </div>

      <div className="p-5">
        <h3 className="font-display text-lg font-semibold text-foreground group-hover:text-accent transition-colors duration-300">
          {apartment.name}
        </h3>
        <p className="mt-2 font-body text-sm text-muted-foreground line-clamp-2 leading-relaxed">
          {apartment.shortDescription || apartment.description}
        </p>
        {apartment.features.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {apartment.features.slice(0, 3).map((f) => (
              <span key={f} className="px-2.5 py-0.5 rounded-lg bg-muted text-muted-foreground text-xs font-body">
                {f}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-center gap-1.5 text-accent-strong text-sm font-body font-semibold group-hover:gap-3 transition-all duration-300">
          Details ansehen <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
});

ApartmentCard.displayName = "ApartmentCard";

export default ApartmentCard;
